import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MOCK_RESPONDERS, DISPATCH_STATUS_CONFIG } from 'utils/mockData';
import {
  subscribeToDispatch, getDispatchChain,
  initDispatch, respondToDispatch, startTimeoutWatcher,
} from 'services/dispatchStore';
import toast from 'react-hot-toast';

function Countdown({ timeoutAt }) {
  const [left, setLeft] = useState(Math.max(0, Math.round((timeoutAt - Date.now()) / 1000)));
  useEffect(() => {
    const t = setInterval(() => setLeft(Math.max(0, Math.round((timeoutAt - Date.now()) / 1000))), 500);
    return () => clearInterval(t);
  }, [timeoutAt]);
  if (left <= 0) return <span className="text-xs font-mono text-crimson">Escalating...</span>;
  return (
    <span className="text-xs font-mono text-amber flex items-center gap-1">
      ⏱ {left}s to auto-escalate
    </span>
  );
}

function ResponderCard({ dispatch, responder, isCurrentUser, incidentId, onAction }) {
  const cfg = DISPATCH_STATUS_CONFIG[dispatch.status] || DISPATCH_STATUS_CONFIG.standby;
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  if (!responder) return null;

  const handleAccept = () => {
    respondToDispatch(incidentId, responder.id, 'accept');
    toast.success(`Mission accepted — navigating to incident`, { icon: '✅' });
    onAction?.('accept', responder.id);
  };

  const handleDecline = () => {
    if (!declineReason.trim()) return;
    respondToDispatch(incidentId, responder.id, 'decline', declineReason);
    setDeclining(false);
    toast('Declined — escalating to next responder', { icon: '⬇️' });
    onAction?.('decline', responder.id);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border overflow-hidden"
      style={{ borderColor: cfg.color + '40', background: cfg.bg }}
    >
      <div className="p-4 flex items-start gap-3">
        {/* Avatar */}
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
          style={{ background: cfg.color + '30', color: cfg.color, border: `1.5px solid ${cfg.color}` }}
        >
          {responder.avatar}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-bold text-sm text-white">{responder.name}</span>
            <span className="text-xs font-mono text-gray-400">{responder.badge}</span>
            {dispatch.status === 'standby' && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-electric/10 border border-electric/20 text-electric">
                STANDBY
              </span>
            )}
          </div>
          <div className="text-xs text-gray-400 mb-1">{responder.role} · {responder.distance} · ETA {responder.eta}</div>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{ background: cfg.color + '20', color: cfg.color }}
            >
              {cfg.icon} {cfg.label}
            </span>
            {dispatch.status === 'notified' && dispatch.timeoutAt && (
              <Countdown timeoutAt={dispatch.timeoutAt} />
            )}
            {dispatch.status === 'standby' && (
              <span className="text-xs text-gray-500">Will be activated if #1 declines</span>
            )}
            {dispatch.status === 'declined' && dispatch.reason && (
              <span className="text-xs text-gray-500 italic">"{dispatch.reason}"</span>
            )}
          </div>
        </div>

        {/* Action buttons (only for the current responder, only if notified) */}
        {isCurrentUser && dispatch.status === 'notified' && (
          <div className="flex gap-2 flex-shrink-0">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleAccept}
              className="px-4 py-2 rounded-lg font-bold text-xs text-navy bg-emerald hover:brightness-110 transition-all shadow-glow-green"
            >
              ✅ Accept
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setDeclining(true)}
              className="px-4 py-2 rounded-lg font-bold text-xs text-crimson border border-crimson/40 bg-crimson/10 hover:bg-crimson/20 transition-all"
            >
              ✕ Decline
            </motion.button>
          </div>
        )}
      </div>

      {/* Decline reason input */}
      <AnimatePresence>
        {isCurrentUser && declining && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-4 pb-4 overflow-hidden"
          >
            <div className="flex gap-2 mt-1">
              <input
                autoFocus
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Reason for declining (e.g. On critical case, Out of jurisdiction...)"
                className="flex-1 bg-navy border border-navy-border rounded-lg px-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-crimson"
              />
              <button
                onClick={handleDecline}
                disabled={!declineReason.trim()}
                className="px-3 py-2 rounded-lg text-xs font-bold bg-crimson text-white disabled:opacity-50"
              >
                Confirm
              </button>
              <button
                onClick={() => setDeclining(false)}
                className="px-3 py-2 rounded-lg text-xs text-gray-400 border border-navy-border"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function DispatchChain({ incident, currentResponderId = null, onActionComplete }) {
  const [chain, setChain] = useState([]);

  useEffect(() => {
    // Initialize dispatch state from incident data
    initDispatch(incident.id, incident.dispatchChain || []);
    setChain(getDispatchChain(incident.id));

    // Subscribe to live updates
    const unsub = subscribeToDispatch(incident.id, (newChain) => {
      setChain([...newChain]);
    });

    // Start timeout watcher
    const stopWatcher = startTimeoutWatcher(incident.id);

    return () => {
      unsub();
      stopWatcher();
    };
  }, [incident.id]);

  const primaryResponder = chain.find((d) => d.status === 'accepted' || d.status === 'notified');
  const hasActiveResponder = chain.some((d) => d.status === 'accepted' || d.status === 'en_route' || d.status === 'on_scene');

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display font-bold text-sm text-white">🚨 Dispatch Chain</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Smart queue — 2 nearest notified simultaneously · Primary acts, standby is ready
          </p>
        </div>
        {hasActiveResponder && (
          <span className="text-xs font-mono px-2 py-1 rounded-full bg-emerald/10 border border-emerald/30 text-emerald">
            ● Responding
          </span>
        )}
      </div>

      {/* Chain legend */}
      <div className="flex gap-3 p-2.5 rounded-lg bg-navy border border-navy-border text-xs text-gray-400">
        <span>🟢 PRIMARY notified first</span>
        <span>·</span>
        <span>⏳ STANDBY gets alerted if #1 declines</span>
        <span>·</span>
        <span>⚡ Auto-escalates in 30s</span>
      </div>

      {/* Chain cards */}
      <div className="space-y-2">
        {chain.map((dispatch, idx) => {
          const responder = MOCK_RESPONDERS.find((r) => r.id === dispatch.responderId);
          return (
            <div key={dispatch.responderId} className="relative">
              {/* Position badge */}
              <div
                className="absolute -left-1 -top-1 z-10 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                style={{
                  background: idx === 0 ? '#FFB300' : '#1E90FF',
                  color: '#0A0F1E',
                }}
              >
                {idx + 1}
              </div>
              <ResponderCard
                dispatch={dispatch}
                responder={responder}
                isCurrentUser={currentResponderId === dispatch.responderId}
                incidentId={incident.id}
                onAction={onActionComplete}
              />
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {chain.length === 0 && (
        <div className="text-center py-6 text-gray-400 text-sm glass-card">
          <div className="text-3xl mb-2">📡</div>
          No responders dispatched yet
        </div>
      )}
    </div>
  );
}
